import { useEffect, useState } from 'react';
import { confirm } from '../utils/confirm';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import * as tasksApi from '../api/tasks';
import { listGroups } from '../api/groups';
import { searchCustomers } from '../api/customers';
import { myStaff } from '../api/users';
import ResultStatusesField from '../components/ResultStatusesField';
import CustomerRevenueChartModal from '../components/CustomerRevenueChartModal';
import { fmtDate, fmtDateTime, fmtVND, taskCompletion, isLate } from '../utils/format';
import { StatusChip } from '../components/Chips';

// Khóa định danh item: việc quét -> custId; việc đơn hàng -> custCode.
function itemKey(it) {
  return it.custId ? it.custId._id : it.custCode;
}
// Thông tin KH để hiển thị (việc quét có Customer; việc đơn hàng dùng ảnh chụp).
function itemCust(it) {
  if (it.custId) return it.custId;
  return { code: it.custCode, name: it.custName, sale: it.custSale, ratio: null, debt: null };
}

// Thanh tiến độ 2 màu: phần xanh = đã hoàn thành, phần đỏ = chưa hoàn thành.
// Thanh tiến độ theo MÀU NGỮ NGHĨA (hệ thiết kế mới): track xám, fill đổi màu theo ngưỡng
// tiến độ công việc — ≥50% xanh, 20–49% hổ phách, <20% đỏ (thay kiểu nền đỏ toàn bộ cũ).
function TwoColorBar({ pct, style }) {
  const p = Math.max(0, Math.min(100, Math.round(pct || 0)));
  const color = p >= 50 ? 'var(--ok)' : p >= 20 ? 'var(--warn-fill)' : 'var(--danger)';
  return (
    <div style={{ height: 8, background: 'var(--track)', borderRadius: 5, overflow: 'hidden', ...style }}>
      <div style={{ width: `${p}%`, height: '100%', background: color, borderRadius: 5 }}></div>
    </div>
  );
}

// Số ngày còn lại tới hạn (>0 = còn, 0 = hôm nay, <0 = quá hạn). null nếu không có hạn.
function daysLeftOf(dl) {
  if (!dl) return null;
  const d = new Date(dl);
  if (isNaN(d)) return null;
  const a = new Date(); a.setHours(0, 0, 0, 0);
  const b = new Date(d); b.setHours(0, 0, 0, 0);
  return Math.round((b - a) / 86400000);
}
// Lệch NGÀY giữa 2 mốc (kết quả = số ngày của b so với a; >0 = b sau a).
function dayDiff(a, b) {
  const x = new Date(a); x.setHours(0, 0, 0, 0);
  const y = new Date(b); y.setHours(0, 0, 0, 0);
  return Math.round((y - x) / 86400000);
}
// Ngày HOÀN THÀNH của 1 hàng (đã done) = mốc muộn nhất trong các đầu việc (doneAt; cũ chưa có -> updatedAt).
function rowDoneAt(tasks) {
  let t = 0;
  tasks.forEach((x) => { const d = new Date(x.doneAt || x.updatedAt || 0).getTime(); if (d > t) t = d; });
  return t || null;
}
// Viết tắt tên cho avatar người tạo (2 chữ cái).
function nameInitials(name) {
  const p = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  return ((p.length > 1 ? p[p.length - 2][0] : '') + p[p.length - 1][0]).toUpperCase();
}
// Trạng thái 1 nhiệm vụ (nhóm nhiều đầu việc hoặc lẻ): done nếu TẤT CẢ đã xong; overdue nếu CÓ việc quá hạn
// (isLate đã loại việc done); còn lại doing.
function rowStatusOf(tasks) {
  if (tasks.every((t) => t.status === 'done')) return 'done';
  if (tasks.some((t) => isLate(t))) return 'overdue';
  return 'doing';
}

// 1 thẻ thống kê ở đầu màn (tổng/hoàn thành/đang chạy/quá hạn/tỷ lệ).
function StatCard({ icon, tone, label, value, sub }) {
  return (
    <div className={'task-stat tone-' + tone}>
      <div className="task-stat-ico">{icon}</div>
      <div className="task-stat-body">
        <div className="task-stat-label">{label}</div>
        <div className="task-stat-value">{value}</div>
        <div className="task-stat-sub">{sub}</div>
      </div>
    </div>
  );
}

export default function TasksPage() {
  const { me } = useAuth();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Lọc theo NHÓM KINH DOANH của NGƯỜI TẠO việc ('' = tất cả · 'gd' = việc Giám đốc giao).
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [showAll, setShowAll] = useState(false); // "Xem tất cả" -> nạp cả việc đã xong quá cũ (>6 tháng)
  const [page, setPage] = useState(1); // phân trang bảng
  const [pageSize, setPageSize] = useState(20);

  function reload(all = showAll) {
    setLoading(true);
    return tasksApi
      .listTasks(all)
      .then(setTasks)
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reload();
    // Chỉ GĐ/TP mới thấy việc của nhiều nhóm -> NV không cần bộ lọc.
    if (me.role === 'GD' || me.role === 'TP' || me.isDeputy) listGroups().then(setGroups).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lọc theo THỜI GIAN GIAO việc (ngày tạo): tất cả / hôm nay / tuần này (từ thứ 2).
  const [timeFilter, setTimeFilter] = useState('all');
  const inTime = (t) => {
    if (timeFilter === 'all') return true;
    const d = new Date(t.createdAt);
    if (isNaN(d)) return true;
    const now = new Date();
    if (timeFilter === 'today') return d.toDateString() === now.toDateString();
    const mon = new Date(now);
    mon.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // thứ 2 tuần này
    mon.setHours(0, 0, 0, 0);
    return d >= mon;
  };

  // Nhóm của việc = nhóm của NGƯỜI TẠO (nhân viên/quản lý nhóm nào tạo thì việc thuộc nhóm đó).
  // Giám đốc KHÔNG thuộc nhóm nào -> việc GĐ giao để TAB RIÊNG, không lẫn vào nhóm nào.
  // CHỈ role 'GD'. Cố vấn (CV) VẪN thuộc nhóm (vd Nguyễn Huyền Như là CV nhóm GTS) -> lọc theo nhóm
  // của họ như người thường, gộp vào GĐ là sai.
  // Người tạo thuộc Phòng CSKH -> tab "CSKH giao" (ưu tiên sau GĐ, trước nhóm).
  const isGD = (t) => t?.createdBy?.role === 'GD';
  const isCskh = (t) => !isGD(t) && (t?.createdBy?.departments || []).includes('Phòng CSKH');
  const groupIdOf = (t) => String(t?.createdBy?.group?._id || t?.createdBy?.group || '');
  const matchTab = (t, key) =>
    key === '' ? true : key === 'gd' ? isGD(t) : key === 'cskh' ? isCskh(t) : !isGD(t) && !isCskh(t) && groupIdOf(t) === key;

  const timeFiltered = tasks.filter(inTime);
  const shown = timeFiltered.filter((t) => matchTab(t, groupFilter));
  // Số VIỆC được giao trong tab = số nhiệm vụ (các đầu việc cùng batchKey tính là 1, khớp số thẻ hiển thị).
  const countOf = (key) => new Set(timeFiltered.filter((t) => matchTab(t, key)).map((t) => t.batchKey || t._id)).size;
  const hasGD = tasks.some(isGD); // chỉ hiện tab khi thực sự có việc như vậy
  const hasCskh = tasks.some(isCskh);
  // Bộ lọc nhóm hiển thị: GĐ/CV thấy MỌI nhóm; TRƯỞNG PHÒNG chỉ thấy nhóm MÌNH quản lý
  // (Hồng Yến GTS chỉ cần GĐ giao + GTS + CSKH, không cần Gold/Hachi/HCM).
  const isAdmin = me.role === 'GD' || me.role === 'CV';
  const myGroupId = String(me.group?.id || me.group?._id || me.group || '');
  const tabGroups = isAdmin ? groups : groups.filter((g) => String(g._id) === myGroupId);

  function roleSubtitle() {
    if (me.role === 'GD') return 'Toàn bộ công việc các phòng';
    if (me.role === 'TP') return 'Công việc của nhóm bạn — nhận & giao cho nhân viên';
    return 'Công việc được giao cho bạn';
  }

  async function openCreateTask() {
    const groups = await listGroups();
    openModal(
      <AssignTaskModal
        me={me}
        groups={groups}
        onCancel={closeModal}
        onSubmit={async (payload) => {
          try {
            const res = await tasksApi.createTask(payload);
            closeModal();
            toast(res?.message || 'Đã tạo & giao công việc');
            await reload();
          } catch (err) {
            toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
          }
        }}
      />
    );
  }

  function openDetail(taskId, onBack) {
    openModal(
      <TaskDetailModal
        taskId={taskId}
        onClose={closeModal}
        onBack={onBack}
        onChanged={async () => {
          await reload();
        }}
      />,
      { wide: true }
    );
  }

  // Chi tiết 1 "đầu việc" gồm nhiều nhân viên (theo batchKey): bảng theo từng nhân viên.
  // Bấm 1 NV -> mở chi tiết KH, kèm nút "Quay lại" để trở về bảng này.
  function openGroupDetail(subTasks) {
    openModal(
      <GroupDetailModal
        tasks={subTasks}
        onOpenTask={(taskId) => openDetail(taskId, () => openGroupDetail(subTasks))}
        onClose={closeModal}
        onChanged={reload}
      />,
      { wide: true }
    );
  }

  // GĐ xóa công việc ngay từ thẻ ở màn chính (xóa cả các đầu việc cùng batchKey).
  async function deleteTasks(ids, label) {
    if (!await confirm(`Xóa công việc "${label}"? Hành động này không thể hoàn tác.`)) return;
    try {
      for (const id of ids) await tasksApi.deleteTask(id);
      toast('Đã xóa công việc', 'warn');
      await reload();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }
  // GĐ xóa mọi việc; TP chỉ xóa việc do CHÍNH MÌNH tạo (không xóa việc của Giám đốc).
  const myId = String(me.id || me._id);
  const canDeleteTask = (t) =>
    me.role === 'GD' || (me.role === 'TP' && String(t?.createdBy?._id || t?.createdBy || '') === myId);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [groupFilter, timeFilter, showAll]); // đổi bộ lọc -> về trang 1

  // ---- HÀNG bảng: mỗi hàng = 1 NHIỆM VỤ (gộp đầu việc cùng batchKey, hoặc việc lẻ), mới nhất lên đầu ----
  const rows = (() => {
    const gm = new Map();
    const singles = [];
    shown.forEach((t) => {
      if (t.batchKey) { if (!gm.has(t.batchKey)) gm.set(t.batchKey, []); gm.get(t.batchKey).push(t); }
      else singles.push(t);
    });
    return [
      ...[...gm.entries()].map(([key, tasks]) => ({ key, group: true, tasks })),
      ...singles.map((t) => ({ key: t._id, group: false, tasks: [t] })),
    ].sort((a, b) => {
      // 1) CHƯA hoàn thành luôn lên trên; đã hoàn thành xuống dưới.
      const aDone = rowStatusOf(a.tasks) === 'done';
      const bDone = rowStatusOf(b.tasks) === 'done';
      if (aDone !== bDone) return aDone ? 1 : -1;
      // 2) Cùng nhóm -> theo THỜI GIAN TẠO công việc (mới nhất trước).
      return new Date(b.tasks[0].createdAt || 0) - new Date(a.tasks[0].createdAt || 0);
    });
  })();
  // 5 thẻ thống kê (trên tập ĐANG HIỂN THỊ theo bộ lọc).
  const stat = { total: rows.length, done: 0, doing: 0, overdue: 0 };
  rows.forEach((r) => { stat[rowStatusOf(r.tasks)] += 1; });
  const pctStat = (n) => (stat.total ? Math.round((n / stat.total) * 1000) / 10 : 0);
  const rate = stat.total ? Math.round(((stat.total - stat.overdue) / stat.total) * 100) : 0;
  // Phân trang.
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const curPage = Math.min(page, pageCount);
  const pageRows = rows.slice((curPage - 1) * pageSize, curPage * pageSize);

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Quản lý công việc</h2>
          <div className="sub">
            {roleSubtitle()}
            {!loading && rows.length ? ` · ${rows.length} nhiệm vụ đang chạy` : ''}
          </div>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)} style={{ width: 'auto' }} title="Lọc theo thời gian giao việc">
            <option value="all">📅 Mọi thời gian</option>
            <option value="today">Hôm nay</option>
            <option value="week">Tuần này</option>
          </select>
          <button
            className={'btn' + (showAll ? '' : ' ghost')}
            onClick={() => { const next = !showAll; setShowAll(next); reload(next); }}
            title={showAll ? 'Đang hiện CẢ việc đã xong cũ — bấm để chỉ hiện việc gần đây (nhẹ hơn)' : 'Mặc định ẩn việc ĐÃ XONG > 6 tháng cho nhẹ — bấm để xem tất cả'}
          >
            {showAll ? '📂 Đang xem tất cả' : '🗂 Xem tất cả'}
          </button>
          {(me.role === 'GD' || me.role === 'TP' || me.isDeputy) && (
            <button className="btn" onClick={openCreateTask}>+ Giao việc mới</button>
          )}
        </div>
      </div>

      {/* 5 thẻ thống kê nhiệm vụ (trên tập ĐANG HIỂN THỊ theo bộ lọc) */}
      {!loading && rows.length > 0 && (
        <div className="tasks-stats">
          <StatCard tone="ok" icon="📋" label="Tổng nhiệm vụ" value={stat.total} sub="Đang phụ trách" />
          <StatCard tone="info" icon="✔️" label="Hoàn thành" value={stat.done} sub={`${pctStat(stat.done)}% tổng số`} />
          <StatCard tone="warn" icon="⏳" label="Đang thực hiện" value={stat.doing} sub={`${pctStat(stat.doing)}% tổng số`} />
          <StatCard tone="danger" icon="⚠️" label="Quá hạn" value={stat.overdue} sub={`${pctStat(stat.overdue)}% tổng số`} />
          <StatCard tone="accent" icon="🎯" label="Tỷ lệ hoàn thành" value={`${rate}%`} sub={`${stat.total - stat.overdue}/${stat.total} nhiệm vụ`} />
        </div>
      )}

      {/* Tab lọc theo NGƯỜI GIAO việc (kèm số việc trong khoảng thời gian đang chọn) */}
      {groups.length > 0 && (
        <div className="task-tabs">
          {[
            { k: '', label: 'Tất cả' },
            ...(hasGD ? [{ k: 'gd', label: '🎯 Giám đốc giao' }] : []),
            ...(hasCskh ? [{ k: 'cskh', label: '💬 CSKH giao' }] : []),
            ...tabGroups.map((g) => ({ k: g._id, label: g.name })),
          ].map((t) => (
            <span key={t.k || 'all'} onClick={() => setGroupFilter(t.k)} className={'task-tab' + (groupFilter === t.k ? ' on' : '')}>
              {t.label} ({countOf(t.k)})
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="card"><div className="empty">Đang tải...</div></div>
      ) : !rows.length ? (
        <div className="card"><div className="empty">
          {!tasks.length ? 'Chưa có công việc nào.' : groupFilter === 'gd' ? 'Giám đốc chưa giao việc nào.' : 'Nhóm này chưa tạo công việc nào.'}
        </div></div>
      ) : (
        <div className="card tasks-table">
          <div className="tasks-thead">
            <div>NHIỆM VỤ</div>
            <div>TIẾN ĐỘ</div>
            <div>HẠN HOÀN THÀNH</div>
            <div>NGƯỜI TẠO</div>
            <div className="ttc-act-h">THAO TÁC</div>
          </div>
          {pageRows.map((r) => (
            <TaskTableRow
              key={r.key}
              row={r}
              onOpen={() => (r.group ? openGroupDetail(r.tasks) : openDetail(r.tasks[0]._id))}
              canDelete={canDeleteTask(r.tasks[0])}
              onDelete={() => deleteTasks(r.tasks.map((t) => t._id), r.tasks[0].title)}
            />
          ))}
          <div className="tasks-pager">
            <span className="small muted">Hiển thị {(curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, rows.length)} của {rows.length} nhiệm vụ</span>
            {pageCount > 1 && (
              <div className="tasks-pages">
                <button type="button" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)}>‹</button>
                {Array.from({ length: pageCount }, (_, i) => i + 1)
                  .slice(Math.max(0, Math.min(curPage - 3, pageCount - 5)), Math.max(0, Math.min(curPage - 3, pageCount - 5)) + 5)
                  .map((p) => (
                    <button type="button" key={p} className={p === curPage ? 'on' : ''} onClick={() => setPage(p)}>{p}</button>
                  ))}
                <button type="button" disabled={curPage >= pageCount} onClick={() => setPage(curPage + 1)}>›</button>
              </div>
            )}
            <span className="small muted">
              Hiển thị{' '}
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} style={{ width: 'auto', display: 'inline-block' }}>
                <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
              </select>{' '}nhiệm vụ/trang
            </span>
          </div>
        </div>
      )}
    </>
  );
}

// 1 HÀNG bảng nhiệm vụ — 5 cột: nhiệm vụ / tiến độ / hạn hoàn thành / người tạo / thao tác.
// row = { key, group:bool, tasks:[...] }. Nhóm (batchKey) hoặc việc lẻ đều là 1 hàng.
function TaskTableRow({ row, onOpen, canDelete, onDelete }) {
  const head = row.tasks[0];
  let done = 0, total = 0;
  row.tasks.forEach((t) => { const c = taskCompletion(t); done += c.done; total += c.total; });
  const pct = total ? Math.round((done / total) * 100) : 0;
  const assignedCount = row.tasks.filter((t) => t.assignTo).length;
  const status = rowStatusOf(row.tasks);
  const dl = head.deadline || head.reviewDeadline;
  const diff = daysLeftOf(dl);
  const [menu, setMenu] = useState(false);
  return (
    <div className="task-trow" onClick={onOpen}>
      <div className="ttc-task">
        <div className="ttc-title">
          <span className="ttc-name">{head.title}</span>
          {row.group
            ? <span className="ttag ttag-nv">Nhóm NV{row.tasks.length > 1 ? ` · ${row.tasks.length}` : ''}</span>
            : <StatusChip task={head} />}
        </div>
        {head.detail && <div className="ttc-detail">{head.detail}</div>}
        <div className="ttc-meta">
          {row.group ? `Đã giao ${assignedCount}/${row.tasks.length}` : `Phụ trách ${head.assignTo ? head.assignTo.name : 'chưa giao'}`}
          {` · Hạn NV ${fmtDate(head.deadline)} · Hạn duyệt ${fmtDate(head.reviewDeadline)}`}
          {head.createdBy ? ` · Tạo bởi ${head.createdBy.name}` : ''}
          {head.createdAt ? ` · ${fmtDate(head.createdAt)}` : ''}
        </div>
      </div>

      <div className="ttc-prog">
        <div className="ttc-prog-top">
          <span className="muted small">{done}/{total} KH đạt</span>
          <span className="ttc-pct" style={{ color: pct >= 50 ? 'var(--ok)' : pct >= 20 ? 'var(--warn)' : 'var(--danger)' }}>{pct}%</span>
        </div>
        <TwoColorBar pct={pct} />
      </div>

      <div className="ttc-dl">
        <div className="ttc-dl-date">📅 {fmtDate(dl)}</div>
        {(() => {
          // ĐÃ HOÀN THÀNH: so NGÀY HOÀN THÀNH với hạn (không phải hôm nay).
          //  - hoàn thành đúng/trước hạn -> "Đúng hạn".
          //  - hoàn thành sau hạn -> "Quá hạn N ngày" (N = từ hạn tới ngày ấn hoàn thành).
          if (status === 'done' && dl) {
            const at = rowDoneAt(row.tasks);
            const over = at ? dayDiff(dl, at) : 0;
            return over > 0
              ? <div className="ttc-dl-left over">Quá hạn {over} ngày</div>
              : <div className="ttc-dl-left okleft">✓ Đúng hạn</div>;
          }
          // CHƯA hoàn thành: tính theo hôm nay như cũ.
          if (diff == null) return null;
          return (
            <div className={'ttc-dl-left ' + (status === 'overdue' ? 'over' : diff < 0 ? 'muted-left' : diff <= 2 ? 'soon' : 'okleft')}>
              {diff < 0 ? `Quá hạn ${-diff} ngày` : diff === 0 ? 'Hôm nay' : `Còn ${diff} ngày`}
            </div>
          );
        })()}
      </div>

      <div className="ttc-creator">
        {head.createdBy && (
          <>
            <span className="ttc-ava">{nameInitials(head.createdBy.name)}</span>
            <div className="ttc-cwrap">
              <div className="ttc-cname">{head.createdBy.name}</div>
              <div className="ttc-cdate">{fmtDate(head.createdAt)}</div>
            </div>
          </>
        )}
      </div>

      <div className="ttc-act" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="btn sm ghost" onClick={onOpen}>Chi tiết →</button>
        <div className="ttc-menu">
          <button type="button" className="ttc-dots" onClick={() => setMenu((v) => !v)} aria-label="Thao tác">⋮</button>
          {menu && (
            <div className="ttc-menu-pop" onMouseLeave={() => setMenu(false)}>
              <button type="button" onClick={() => { setMenu(false); onOpen(); }}>Xem chi tiết</button>
              {canDelete && <button type="button" className="danger" onClick={() => { setMenu(false); onDelete(); }}>Xóa nhiệm vụ</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Màn chi tiết 1 đầu việc nhiều nhân viên: bảng theo từng nhân viên, bấm để xem KH của NV đó.
function GroupDetailModal({ tasks, onOpenTask, onClose, onChanged }) {
  const { me } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState(tasks); // bản cục bộ để làm mới sau khi giao việc
  const head = rows[0];
  let done = 0;
  let total = 0;
  rows.forEach((t) => {
    const c = taskCompletion(t);
    done += c.done;
    total += c.total;
  });
  const pct = total ? Math.round((done / total) * 100) : 0;

  // TP của nhóm: có thể "giao việc" hàng loạt cho các đầu việc đang chờ xác nhận.
  const isTPofGroup = me.role === 'TP' && String(head.group?._id) === String(me.group?.id);
  const pendingTasks = rows.filter((t) => t.status === 'pending_confirm');

  // Tải lại trạng thái mới của các đầu việc (giữ nguyên modal).
  async function refreshRows() {
    const updated = await Promise.all(rows.map((t) => tasksApi.getTask(t._id).catch(() => t)));
    setRows(updated);
  }

  async function confirmAll() {
    if (!pendingTasks.length) return;
    try {
      for (const t of pendingTasks) await tasksApi.confirmTask(t._id);
      toast(`Đã giao việc cho ${pendingTasks.length} nhân viên`);
      await refreshRows(); // cập nhật ngay trên màn này, KHÔNG đóng
      await onChanged(); // đồng bộ lại bảng nền
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  return (
    <>
      <span className="close" onClick={onClose}>
        ×
      </span>
      <h3>{head.title}</h3>
      <div className="small muted" style={{ marginBottom: 10 }}>
        {head.detail}
      </div>
      <div className="row between small" style={{ marginBottom: 4 }}>
        <span className="muted">
          Tiến độ chung {done}/{total} KH đạt
        </span>
        <span style={{ fontWeight: 700 }}>{pct}%</span>
      </div>
      <TwoColorBar pct={pct} style={{ marginBottom: 14 }} />

      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Chi tiết theo từng nhân viên ({rows.length})</div>
      <div className="tbl-wrap">
      <table>
        <thead>
          <tr>
            <th>Nhân viên phụ trách</th>
            <th>Nhóm</th>
            <th className="c">Số KH</th>
            <th className="c">Tiến độ</th>
            <th className="c">Trạng thái</th>
            <th className="c"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => {
            const c = taskCompletion(t);
            return (
              <tr key={t._id}>
                <td>{t.assignTo ? t.assignTo.name : <span className="muted">Chưa giao</span>}</td>
                <td>{t.group?.name || '—'}</td>
                <td className="c">{c.total}</td>
                <td className="c">
                  {c.done}/{c.total}
                </td>
                <td className="c">
                  <StatusChip task={t} />
                </td>
                <td className="c">
                  <button className="btn sm ghost" onClick={() => onOpenTask(t._id)}>
                    Xem →
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        {isTPofGroup && pendingTasks.length > 0 && (
          <button className="btn ok" onClick={confirmAll}>
            ✅ Giao việc cho tất cả ({pendingTasks.length} nhân viên)
          </button>
        )}
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onClose}>
          Đóng
        </button>
      </div>
    </>
  );
}


function TaskDetailModal({ taskId, onClose, onBack, onChanged }) {
  const { me } = useAuth();
  const toast = useToast();
  const [task, setTask] = useState(null);
  const [staff, setStaff] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [notes, setNotes] = useState({});
  const [picks, setPicks] = useState({}); // trạng thái kết quả NV chọn theo item
  const [reviewNotes, setReviewNotes] = useState({}); // ghi chú TP theo item
  const [replyText, setReplyText] = useState(''); // ô nhập trao đổi
  const [chart, setChart] = useState(null); // { code, name } — mở biểu đồ doanh thu lũy kế của KH
  // GĐ/CV: bấm tên KH -> xem biểu đồ doanh thu lũy kế toàn bộ các năm (giống màn "Doanh thu lũy kế").
  const isAdminView = me.role === 'GD' || me.role === 'CV';
  const custNameLink = (c) =>
    isAdminView && c.code ? (
      <span
        style={{ cursor: 'pointer', color: 'var(--accent)', textDecoration: 'underline' }}
        title="Xem biểu đồ doanh thu lũy kế của KH"
        onClick={() => setChart({ code: c.code, name: c.name })}
      >
        {c.name}
      </span>
    ) : (
      c.name
    );

  async function load() {
    const t = await tasksApi.getTask(taskId);
    setTask(t);
    const initial = {};
    const initPicks = {};
    const initReview = {};
    t.items.forEach((it) => {
      initial[itemKey(it)] = it.note || '';
      initPicks[itemKey(it)] = it.resultStatus || '';
      initReview[itemKey(it)] = it.reviewNote || '';
    });
    setNotes(initial);
    setPicks(initPicks);
    setReviewNotes(initReview);
  }

  const [editing, setEditing] = useState(false);

  useEffect(() => {
    load();
  }, [taskId]);

  if (!task) return null;

  if (editing) {
    return (
      <EditTaskForm
        task={task}
        onCancel={() => setEditing(false)}
        onSaved={async () => {
          setEditing(false);
          await load();
          await onChanged();
        }}
      />
    );
  }

  const assignee = task.assignTo;
  const canEdit = me.role === 'GD';
  // PHÓ PHÒNG quản NV này? (assignTo ∈ deputyStaff) -> được giao/duyệt như TP, giới hạn tập con.
  const ppOfTask = !!(me.isDeputy && task.assignTo && (me.deputyStaff || []).some((id) => String(id) === String(task.assignTo?._id || task.assignTo)));
  const canAssign = (me.role === 'TP' && String(task.group?._id) === String(me.group?.id)) || ppOfTask;
  const isOwnerNV = me.role === 'NV' && String(task.assignTo?._id) === String(me.id);
  const canReview = (me.role === 'TP' && String(task.group?._id) === String(me.group?.id)) || ppOfTask;
  // TP tự care KH (assignTo = chính TP): tự điền kết quả + kết thúc trực tiếp, không cần giao NV.
  const isSelfLeadTask = me.role === 'TP' && String(task.assignTo?._id) === String(me.id) && String(task.group?._id) === String(me.group?.id);
  // Sau khi đã hoàn tất duyệt (done) -> TP không sửa duyệt/ghi chú nữa.
  const canReviewNow = canReview && task.status !== 'done';
  // Ô "Ghi chú TP" + nút duyệt: chỉ cho luồng NV nộp → TP duyệt (ẩn khi TP tự làm KH của mình).
  const showReview = canReviewNow && !isSelfLeadTask;
  const comp = taskCompletion(task);

  async function saveNote(custId) {
    const note = notes[custId];
    if (!note || !note.trim()) return toast('Nhập kết quả trước khi lưu', 'warn');
    const hasStatuses = task.statuses && task.statuses.length;
    if (hasStatuses && !picks[custId]) return toast('Hãy chọn trạng thái kết quả cho KH này', 'warn');
    try {
      await tasksApi.saveItemNote(task._id, custId, note, picks[custId] || '');
      toast('Đã lưu kết quả KH');
      await load();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function doSubmit() {
    try {
      await tasksApi.submitTask(task._id);
      toast('Đã nộp, chờ trưởng phòng duyệt');
      await load();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function doReply() {
    if (!replyText.trim()) return;
    try {
      await tasksApi.replyTask(task._id, replyText.trim());
      setReplyText('');
      await load();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function doConfirm() {
    try {
      await tasksApi.confirmTask(task._id);
      toast('Đã xác nhận, nhân viên đã có thể thấy công việc');
      await load();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function doReview(custId, newState) {
    await tasksApi.reviewItem(task._id, custId, newState, reviewNotes[custId] || '');
    await load();
    await onChanged();
  }

  async function doFinalize() {
    try {
      await tasksApi.finalizeTask(task._id);
      toast('Đã hoàn tất duyệt công việc');
      onClose();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function loadStaffAndOpenAssign() {
    const own = await myStaff().catch(() => []);
    setStaff(own);
    setAssignOpen(true);
  }

  async function doAssign(userId) {
    try {
      await tasksApi.assignTask(task._id, userId);
      toast('Đã giao việc');
      setAssignOpen(false);
      await load();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  // TP tự care KH: lưu hết kết quả đang nhập rồi kết thúc (tự duyệt Đạt) trong 1 lần.
  async function doSelfFinish() {
    const hasStatuses = task.statuses && task.statuses.length > 0;
    try {
      for (const it of task.items) {
        const k = itemKey(it);
        const note = (notes[k] || '').trim();
        if (!note) return toast('Còn KH chưa ghi kết quả xử lý', 'warn');
        if (hasStatuses && !picks[k]) return toast('Hãy chọn trạng thái kết quả cho tất cả KH', 'warn');
        if (note !== (it.note || '') || (hasStatuses && (picks[k] || '') !== (it.resultStatus || ''))) {
          await tasksApi.saveItemNote(task._id, k, note, picks[k] || '');
        }
      }
      await tasksApi.selfFinishTask(task._id);
      toast('Đã hoàn thành công việc');
      onClose();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  return (
    <>
      <span className="close" onClick={onClose}>
        ×
      </span>
      {onBack && (
        <button className="btn ghost sm" style={{ marginBottom: 10 }} onClick={onBack}>
          ← Quay lại
        </button>
      )}
      <div className="row between" style={{ alignItems: 'flex-start' }}>
        <h3>{task.title}</h3>
        {canEdit && (
          <button className="btn ghost sm" onClick={() => setEditing(true)}>
            ✏️ Sửa yêu cầu
          </button>
        )}
      </div>
      <div className="small muted" style={{ marginBottom: 8 }}>
        {task.detail}
      </div>
      <div className="row" style={{ gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <span className="small">
          Nhóm: <b>{task.group?.name || '—'}</b>
        </span>
        <span className="small">
          Phụ trách: <b>{assignee ? assignee.name : 'Chưa giao'}</b>
        </span>
        <span className="small">
          Hạn NV: <b>{fmtDate(task.deadline)}</b>
        </span>
        <span className="small">
          Hạn duyệt: <b>{fmtDate(task.reviewDeadline)}</b>
        </span>
        <span className="small">
          Hoàn thành:{' '}
          <b style={{ color: comp.pct >= 80 ? 'var(--ok)' : comp.pct >= 50 ? 'var(--warn)' : 'var(--danger)' }}>
            {comp.pct}%
          </b>{' '}
          ({comp.done}/{comp.total})
        </span>
      </div>

      {task.periodMid && (
        <div className="small muted" style={{ marginBottom: 12 }}>
          Số liệu so sánh — Tuần trước: <b>{fmtDate(task.periodFrom)}</b> –{' '}
          <b>{fmtDate(new Date(new Date(task.periodMid).getTime() - 864e5))}</b> · Tuần này:{' '}
          <b>{fmtDate(task.periodMid)}</b> – <b>{fmtDate(task.periodTo)}</b>
        </div>
      )}

      {task.statuses && task.statuses.length > 0 && <StatusBreakdown task={task} />}

      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
        Danh sách khách hàng cần xử lý ({task.items.length})
      </div>

      {task.items.length === 0 ? (
        <div className="empty">Chưa gắn KH</div>
      ) : (
        (() => {
          const hasDeposit = task.items.some((it) => itemCust(it).ratio != null);
          const hasRevenue = task.items.some((it) => it.currRevenue != null);
          const hasStatuses = task.statuses && task.statuses.length > 0;
          const ratioColor = (r) => (r >= 100 ? 'var(--ok)' : r >= 50 ? 'var(--warn)' : 'var(--danger)');
          const stateChipOf = (it) => {
            if (it.state === 'pending') return <span className="chip todo">Chưa làm</span>;
            if (it.state === 'noted')
              return (
                <>
                  <span className="chip review">Chờ duyệt</span>
                  {it.notedLate && <span className="chip late"> Trễ</span>}
                </>
              );
            if (it.state === 'approved')
              return (
                <>
                  <span className="chip done">Đạt</span>
                  {it.notedLate && <span className="chip late"> (trễ)</span>}
                </>
              );
            if (it.state === 'rejected') return <span className="chip late">Không đạt</span>;
            return null;
          };

          return (
            <>
            {/* MOBILE: mỗi KH 1 card (thiết kế) — bỏ 'Sale', thay bằng NỘI DUNG GIẢI TRÌNH của sale. */}
            <div className="only-mobile tk-cards">
              {task.items.map((it) => {
                const c = itemCust(it);
                const key = itemKey(it);
                const editable =
                  (isOwnerNV || isSelfLeadTask || isAdminView) && // GĐ/CV note THAY cho mọi NV
                  ['assigned', 'doing'].includes(task.status) &&
                  ['pending', 'noted', 'rejected'].includes(it.state);
                return (
                  <div key={key} className="tk-card">
                    <div className="tk-card-top">
                      <div className="tk-card-cust"><b>{custNameLink(c)}</b>{c.code ? <span className="small muted"> · {c.code}</span> : null}</div>
                      <span className="tk-card-state">{stateChipOf(it)}</span>
                    </div>
                    {editable ? (
                      <>
                        {hasStatuses && (
                          <select value={picks[key] || ''} onChange={(e) => setPicks({ ...picks, [key]: e.target.value })} style={{ width: '100%', marginBottom: 6 }}>
                            <option value="">— Chọn trạng thái —</option>
                            {task.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        )}
                        <textarea rows={2} style={{ width: '100%' }} placeholder="Ghi kết quả / giải trình xử lý KH này…"
                          value={notes[key] || ''} onChange={(e) => setNotes({ ...notes, [key]: e.target.value })} />
                        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 6 }}>
                          <button className="btn sm" onClick={() => saveNote(key)}>Lưu kết quả</button>
                        </div>
                      </>
                    ) : (
                      <div className="tk-card-note">{it.note ? it.note : <span className="small muted">Chưa có giải trình</span>}</div>
                    )}
                    {showReview && it.state === 'noted' && (
                      <div style={{ marginTop: 6 }}>
                        <input style={{ width: '100%', marginBottom: 6 }} placeholder="Ghi chú khi duyệt…"
                          value={reviewNotes[key] || ''} onChange={(e) => setReviewNotes({ ...reviewNotes, [key]: e.target.value })} />
                        <div className="row" style={{ gap: 6 }}>
                          <button className="btn sm ok" onClick={() => doReview(key, 'approved')}>✓ Đạt</button>
                          <button className="btn sm danger" onClick={() => doReview(key, 'rejected')}>✕ Không đạt</button>
                        </div>
                      </div>
                    )}
                    {showReview && ['approved', 'rejected'].includes(it.state) && (
                      <button className="btn sm ghost" style={{ marginTop: 6 }} onClick={() => doReview(key, 'noted')}>Đổi lại</button>
                    )}
                    {!showReview && it.reviewNote ? <div className="small muted" style={{ marginTop: 4 }}>TP: {it.reviewNote}</div> : null}
                  </div>
                );
              })}
            </div>

            <div className="only-desktop tbl-wrap">
              <table style={{ fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th>Mã KH</th>
                    <th>Tên KH</th>
                    <th>Sale</th>
                    {hasDeposit && <th className="r">Tổng tiền</th>}
                    {hasDeposit && <th className="r">Đã T.Toán</th>}
                    {hasDeposit && <th className="r">Tỷ lệ cọc</th>}
                    {hasDeposit && <th className="r">Công nợ</th>}
                    {hasRevenue && <th className="r">Tuần trước</th>}
                    {hasRevenue && <th className="r">Tuần này</th>}
                    {hasStatuses && <th>Trạng thái KQ</th>}
                    <th style={{ minWidth: 220 }}>Kết quả xử lý</th>
                    <th style={{ minWidth: 180 }}>Ghi chú TP</th>
                    <th className="c">Trạng thái</th>
                    <th className="c">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {task.items.map((it) => {
                    const c = itemCust(it);
                    const key = itemKey(it);
                    // NV chỉ sửa khi việc đang làm (assigned/doing). Sau khi đã NỘP (review) hoặc xong -> khóa.
                    const editable =
                      (isOwnerNV || isSelfLeadTask || isAdminView) && // GĐ/CV note THAY cho mọi NV
                      ['assigned', 'doing'].includes(task.status) &&
                      ['pending', 'noted', 'rejected'].includes(it.state);
                    return (
                      <tr key={key}>
                        <td>{c.code || '—'}</td>
                        <td>{custNameLink(c)}</td>
                        <td className="small">{c.sale || '—'}</td>
                        {hasDeposit && <td className="r">{c.ratio != null ? fmtVND(c.total) : '—'}</td>}
                        {hasDeposit && <td className="r">{c.ratio != null ? fmtVND(c.paid) : '—'}</td>}
                        {hasDeposit && (
                          <td className="r">
                            {c.ratio != null ? <b style={{ color: ratioColor(c.ratio) }}>{c.ratio}%</b> : '—'}
                          </td>
                        )}
                        {hasDeposit && <td className="r">{c.ratio != null ? fmtVND(c.debt) : '—'}</td>}
                        {hasRevenue && (
                          <td className="r small">
                            {it.currRevenue != null ? `${it.prevCount ?? 0} đơn · ${fmtVND(it.prevRevenue || 0)}` : '—'}
                          </td>
                        )}
                        {hasRevenue && (
                          <td className="r small">
                            {it.currRevenue != null ? `${it.currCount ?? 0} đơn · ${fmtVND(it.currRevenue || 0)}` : '—'}
                          </td>
                        )}
                        {hasStatuses && (
                          <td>
                            {editable ? (
                              <select
                                value={picks[key] || ''}
                                onChange={(e) => setPicks({ ...picks, [key]: e.target.value })}
                              >
                                <option value="">— Chọn —</option>
                                {task.statuses.map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </select>
                            ) : it.resultStatus ? (
                              <span className="chip doing">{it.resultStatus}</span>
                            ) : (
                              <span className="small muted">—</span>
                            )}
                          </td>
                        )}
                        <td style={{ minWidth: 220 }}>
                          {editable ? (
                            <textarea
                              rows={2}
                              placeholder="Ghi kết quả xử lý KH này..."
                              value={notes[key] || ''}
                              onChange={(e) => setNotes({ ...notes, [key]: e.target.value })}
                            />
                          ) : it.note ? (
                            it.note
                          ) : (
                            <span className="small muted">Chưa có ghi chú</span>
                          )}
                        </td>
                        <td style={{ minWidth: 180 }}>
                          {showReview ? (
                            <input
                              value={reviewNotes[key] || ''}
                              onChange={(e) => setReviewNotes({ ...reviewNotes, [key]: e.target.value })}
                              placeholder="Ghi chú khi duyệt…"
                            />
                          ) : it.reviewNote ? (
                            it.reviewNote
                          ) : (
                            <span className="small muted">—</span>
                          )}
                        </td>
                        <td className="c">{stateChipOf(it)}</td>
                        <td className="c">
                          {editable && (
                            <button className="btn sm" onClick={() => saveNote(key)}>
                              Lưu
                            </button>
                          )}
                          {showReview && it.state === 'noted' && (
                            <div className="row" style={{ gap: 4, justifyContent: 'center' }}>
                              <button className="btn sm ok" onClick={() => doReview(key, 'approved')}>
                                Đạt
                              </button>
                              <button className="btn sm danger" onClick={() => doReview(key, 'rejected')}>
                                Không
                              </button>
                            </div>
                          )}
                          {showReview && ['approved', 'rejected'].includes(it.state) && (
                            <button className="btn sm ghost" onClick={() => doReview(key, 'noted')}>
                              Đổi lại
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          );
        })()
      )}

      {assignOpen && (
        <div className="card" style={{ background: 'var(--panel2)' }}>
          <h3>Giao việc</h3>
          {!staff.length ? (
            <div className="empty">Phòng bạn chưa có nhân viên</div>
          ) : (
            <div className="field">
              <label>Chọn nhân viên trực tiếp</label>
              <select defaultValue="" onChange={(e) => e.target.value && doAssign(e.target.value)}>
                <option value="" disabled>
                  -- chọn --
                </option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Trao đổi CSKH ↔ TP ↔ sale — hiện cho cả 3, cập nhật mỗi khi có người phản hồi. */}
      <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>💬 Trao đổi ({(task.thread || []).length})</div>
        {(task.thread || []).length ? task.thread.map((m, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <div className="small"><b>{m.byName || '—'}</b> <span className="muted">· {m.role} · {fmtDateTime(m.at)}</span></div>
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{m.text}</div>
          </div>
        )) : <div className="small muted" style={{ marginBottom: 8 }}>Chưa có trao đổi.</div>}
        <div className="row" style={{ gap: 6, marginTop: 8, alignItems: 'flex-start' }}>
          <textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Nhập phản hồi / trao đổi…" style={{ flex: 1 }} />
          <button className="btn sm" onClick={doReply} disabled={!replyText.trim()}>Gửi</button>
        </div>
      </div>

      <div className="row" style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        {canReview && task.status === 'pending_confirm' && (
          <button className="btn sm ok" onClick={doConfirm}>
            ✅ Giao việc cho nhân viên
          </button>
        )}
        {canAssign && task.status === 'pool' && (
          <button className="btn sm" onClick={loadStaffAndOpenAssign}>
            Giao nhân viên
          </button>
        )}
        {canAssign && ['assigned', 'doing', 'review'].includes(task.status) && (
          <button className="btn sm ghost" onClick={loadStaffAndOpenAssign}>
            Đổi người phụ trách
          </button>
        )}
        {isOwnerNV && ['assigned', 'doing'].includes(task.status) && (
          <button className="btn sm ok" onClick={doSubmit}>
            Nộp cho trưởng phòng
          </button>
        )}
        {isSelfLeadTask && ['assigned', 'doing'].includes(task.status) && (
          <button className="btn sm ok" onClick={doSelfFinish}>
            ✅ Kết thúc công việc
          </button>
        )}
        {canReview && ['doing', 'review'].includes(task.status) && ((task.items?.length > 0 && task.items.every((i) => i.state !== 'pending')) || (task.items?.length === 0 && task.status === 'review')) && (
          <button className="btn sm ok" onClick={doFinalize}>
            {task.items?.length === 0 ? '✅ Xác nhận hoàn tất' : 'Hoàn tất duyệt'}
          </button>
        )}
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onClose}>
          Đóng
        </button>
      </div>
      <CustomerRevenueChartModal
        open={!!chart}
        code={chart?.code}
        name={chart?.name}
        onClose={() => setChart(null)}
      />
    </>
  );
}

// "+ Giao việc mới" trên màn Công việc: KHÔNG có bảng KH sẵn (khác Danh sách quét / Quản lý KH),
// nên TÌM & CHỌN khách hàng ngay trong modal rồi tạo việc — tự chia theo NV phụ trách, kèm trạng
// thái kết quả theo TỪNG KH (giống màn CSKH).
function AssignTaskModal({ me, groups, onCancel, onSubmit }) {
  const toast = useToast();
  const canPickGroup = me.role === 'GD' || me.role === 'CV';
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(() => new Map()); // code -> { code, name, sale }
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [group, setGroup] = useState('all');
  const dlDefault = new Date(Date.now() + 3 * 864e5).toISOString().slice(0, 10);
  const [deadline, setDeadline] = useState(dlDefault);
  const [useStatuses, setUseStatuses] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [busy, setBusy] = useState(false);

  async function doSearch(e) {
    e?.preventDefault();
    const term = q.trim();
    if (!term) { setResults([]); return; }
    setSearching(true);
    try { setResults(await searchCustomers(term, true)); }
    catch { setResults([]); }
    finally { setSearching(false); }
  }
  function togglePick(c) {
    setPicked((prev) => {
      const m = new Map(prev);
      m.has(c.code) ? m.delete(c.code) : m.set(c.code, { code: c.code, name: c.name, sale: c.sale });
      return m;
    });
  }
  const pickedArr = [...picked.values()];

  async function submit() {
    if (busy) return;
    if (!title.trim()) return toast('Nhập tiêu đề công việc', 'warn');
    if (!pickedArr.length) return toast('Hãy tìm & chọn ít nhất 1 khách hàng', 'warn');
    setBusy(true);
    try {
      await onSubmit({ title: title.trim(), detail: detail.trim(), group, deadline, statuses: useStatuses ? statuses : [], custCodes: pickedArr.map((c) => c.code) });
    } finally { setBusy(false); }
  }

  return (
    <>
      <span className="close" onClick={onCancel}>×</span>
      <h3>Giao việc mới</h3>
      <div className="demo-hint" style={{ marginTop: 0 }}>
        Chọn khách hàng — việc tự chia theo nhân viên phụ trách (mỗi NV 1 việc kèm KH của mình).
      </div>

      <div className="field">
        <label>1. Tìm &amp; chọn khách hàng ({pickedArr.length} đã chọn)</label>
        <form onSubmit={doSearch} className="row" style={{ gap: 8 }}>
          <input style={{ flex: 1 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nhập mã hoặc tên KH…" />
          <button type="submit" className="btn sm ghost" disabled={searching}>{searching ? '…' : '🔍 Tìm'}</button>
        </form>
        {results.length > 0 && (
          <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginTop: 8 }}>
            {results.map((c) => {
              const on = picked.has(c.code);
              return (
                <div key={c.id || c.code} onClick={() => togglePick(c)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid var(--line)', background: on ? 'var(--panel2)' : 'transparent' }}>
                  <input type="checkbox" checked={on} readOnly style={{ width: 'auto' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13 }}>{c.code} · {c.name}</div>
                    <div className="small muted">Sale: {c.sale || '(chưa rõ)'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!searching && q.trim() && !results.length && <div className="small muted" style={{ marginTop: 6 }}>Không tìm thấy KH nào.</div>}
        {pickedArr.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {pickedArr.map((c) => (
              <span className="tag" key={c.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {c.code} · {c.name}
                <span style={{ cursor: 'pointer', color: 'var(--danger)', fontWeight: 700 }} onClick={() => togglePick(c)}>×</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="field">
        <label>2. Tiêu đề công việc</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="vd: Chăm sóc lại nhóm KH giảm doanh thu" />
      </div>
      <div className="field">
        <label>Yêu cầu chi tiết</label>
        <textarea rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Mô tả công việc cần làm..." />
      </div>
      <div className="two-col">
        {canPickGroup && (
          <div className="field">
            <label>Giao cho nhóm kinh doanh</label>
            <select value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="all">★ Tất cả các nhóm (tự chia theo từng nhân viên)</option>
              {groups.map((g) => (<option key={g._id} value={g._id}>{g.name}</option>))}
            </select>
          </div>
        )}
        <div className="field">
          <label>Hạn nhân viên hoàn thành</label>
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
        </div>
      </div>

      <ResultStatusesField enabled={useStatuses} onEnabled={setUseStatuses} statuses={statuses} onStatuses={setStatuses} />

      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer" />
        <button className="btn ghost" onClick={onCancel}>Hủy</button>
        <button className="btn" onClick={submit} disabled={busy || !title.trim() || !pickedArr.length}>{busy ? 'Đang tạo…' : 'Tạo & giao việc'}</button>
      </div>
    </>
  );
}

// Bảng tỷ lệ KH theo từng trạng thái — tính trên các KH ĐƯỢC DUYỆT ĐẠT.
function StatusBreakdown({ task }) {
  const total = task.items.length;
  const approved = task.items.filter((it) => it.state === 'approved');
  // đếm theo trạng thái (chỉ KH đạt)
  const counts = {};
  task.statuses.forEach((s) => (counts[s] = 0));
  let approvedNoStatus = 0;
  approved.forEach((it) => {
    if (it.resultStatus && counts[it.resultStatus] != null) counts[it.resultStatus]++;
    else approvedNoStatus++;
  });
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  return (
    <div className="card" style={{ background: 'var(--panel2)' }}>
      <h3 style={{ marginBottom: 10 }}>📊 Tỷ lệ KH theo trạng thái (trên KH được duyệt đạt)</h3>
      <div className="small muted" style={{ marginBottom: 10 }}>
        Đã duyệt đạt: <b>{approved.length}</b>/{total} KH
      </div>
      <div className="tbl-wrap">
      <table style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th>Trạng thái</th>
            <th className="c">Số KH đạt</th>
            <th className="r">Tỷ lệ</th>
          </tr>
        </thead>
        <tbody>
          {task.statuses.map((s) => (
            <tr key={s}>
              <td>{s}</td>
              <td className="c">{counts[s]}</td>
              <td className="r">
                <b style={{ color: 'var(--accent2)' }}>{pct(counts[s])}%</b>
              </td>
            </tr>
          ))}
          {approvedNoStatus > 0 && (
            <tr>
              <td className="muted">(Đạt nhưng chưa gắn trạng thái)</td>
              <td className="c">{approvedNoStatus}</td>
              <td className="r">{pct(approvedNoStatus)}%</td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// GD sửa lại yêu cầu công việc: tiêu đề, mô tả, hạn, danh sách trạng thái kết quả.
function EditTaskForm({ task, onCancel, onSaved }) {
  const toast = useToast();
  const [title, setTitle] = useState(task.title || '');
  const [detail, setDetail] = useState(task.detail || '');
  const [deadline, setDeadline] = useState(
    task.deadline ? new Date(task.deadline).toISOString().slice(0, 10) : ''
  );
  const [useStatuses, setUseStatuses] = useState((task.statuses || []).length > 0);
  const [statuses, setStatuses] = useState(task.statuses || []);
  const [statusInput, setStatusInput] = useState('');
  const [busy, setBusy] = useState(false);

  function addStatus() {
    const v = statusInput.trim();
    if (!v || statuses.includes(v)) {
      setStatusInput('');
      return;
    }
    setStatuses([...statuses, v]);
    setStatusInput('');
  }

  async function save() {
    if (busy) return;
    if (!title.trim()) return toast('Nhập tiêu đề', 'warn');
    setBusy(true);
    try {
      await tasksApi.updateTask(task._id, {
        title,
        detail,
        deadline,
        statuses: useStatuses ? statuses : [],
      });
      toast('Đã cập nhật yêu cầu công việc');
      await onSaved();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span className="close" onClick={onCancel}>
        ×
      </span>
      <h3>Sửa yêu cầu công việc</h3>
      <div className="field">
        <label>Tiêu đề công việc</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="field">
        <label>Yêu cầu chi tiết</label>
        <textarea rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} />
      </div>
      <div className="field">
        <label>Hạn nhân viên hoàn thành</label>
        <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
      </div>
      <div className="demo-hint">Trưởng phòng có thêm 1 ngày sau hạn này để kiểm tra & duyệt. Nhóm/người phụ trách/danh sách KH không đổi ở đây.</div>

      <div className="field">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={useStatuses} onChange={(e) => setUseStatuses(e.target.checked)} />
          Dùng trạng thái kết quả cho nhân viên chọn
        </label>
      </div>
      {useStatuses && (
        <div className="card" style={{ background: 'var(--panel2)' }}>
          <div className="row" style={{ gap: 8 }}>
            <input
              style={{ flex: 1 }}
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addStatus())}
              placeholder="Nhập 1 trạng thái rồi bấm Thêm…"
            />
            <button className="btn sm" onClick={addStatus}>
              + Thêm
            </button>
          </div>
          <div style={{ marginTop: 10 }}>
            {statuses.length ? (
              statuses.map((s) => (
                <span className="tag" key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {s}
                  <span style={{ cursor: 'pointer', color: 'var(--danger)', fontWeight: 700 }} onClick={() => setStatuses(statuses.filter((x) => x !== s))}>
                    ×
                  </span>
                </span>
              ))
            ) : (
              <span className="small muted">Chưa có trạng thái nào.</span>
            )}
          </div>
        </div>
      )}

      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onCancel}>
          Hủy
        </button>
        <button className="btn" onClick={save} disabled={busy}>
          {busy ? 'Đang lưu…' : 'Lưu yêu cầu'}
        </button>
      </div>
    </>
  );
}
